import {Injectable} from '@angular/core';
import Echo from 'laravel-echo';
import {environment} from 'src/environments/environment';
import Pusher from 'pusher-js';
import { AppStorageService } from './app-storage.service';
import { StorageKeys } from './storage-keys';

const {api_broadcast, pusher_key, pusher_cluster} = environment;

/**
 * Servicio para gestionar conexiones en tiempo real usando Pusher
 * Permite suscribirse a canales públicos y privados para recibir eventos
 */
@Injectable({
  providedIn: 'root'
})
export class PusherService {
  private token: string;
  private _echo!: Echo<'pusher'>;
  private pusher!: Pusher;
  private isInitialized: boolean = false;

  constructor(private storage: AppStorageService) {
    this.token = this.storage.get(StorageKeys.TOKEN) || '';
  }

  /**
   * Inicializa la conexión con Pusher y configura la instancia de Echo
   * @returns Promesa que se resuelve cuando la conexión se establece correctamente
   */
  init(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isInitialized) {
        resolve();
        return;
      }

      // Refrescar token al momento de inicializar para evitar usar uno obsoleto
      this.token = this.storage.get(StorageKeys.TOKEN) || '';

      this.pusher = new Pusher(pusher_key, {cluster: pusher_cluster});

      this._echo = new Echo<'pusher'>({
        broadcaster: 'pusher',
        key: pusher_key,
        cluster: pusher_cluster,
        encrypted: false,
        authEndpoint: api_broadcast,
        auth: {
          headers: {
            Authorization: `Bearer ${this.token}`,
          }
        }
      });

      this.pusher.connection.bind('connected', () => {
        this.isInitialized = true;
        resolve();
      });

      this.pusher.connection.bind('error', (err: any) => {
        reject(err);
      });
    });
  }

  /**
   * Escucha un evento en un canal público
   * @param channel - Nombre del canal
   * @param event - Nombre del evento
   * @param callback - Función a ejecutar cuando se recibe el evento
   */
  listen(channel: string, event: string, callback: Function) {
    this._echo.channel(channel).listen(event, callback);
  }

  /**
   * Escucha cualquier evento en un canal privado usando bind_global/listenToAll
   * @param channel - Nombre del canal (sin prefijo "private-")
   * @param callback - Función ejecutada para cualquier evento recibido
   */
  listenPrivateAll(channel: string, callback: (eventName: string, data: any) => void) {
    if (!this.isInitialized) {
      return null;
    }

    try {
      const privateChannelName = `private-${channel}`;

      const existingChannel = this.pusher.channel(privateChannelName);
      if (existingChannel) {
        // Limpiar cualquier binding global previo y volver a enlazar
        try { (existingChannel as any).unbind_global && (existingChannel as any).unbind_global(); } catch {}
        (existingChannel as any).bind_global((eventName: string, data: any) => {
          callback(eventName, data);
        });
        return existingChannel;
      }

      // Fallback con Echo si el canal no existe aún en pusher-js
      const echoPrivate = this._echo.private(channel);
      // No hay API de unbind específica, se limpia al salir del canal
      (echoPrivate as any).listenToAll((eventName: string, data: any) => {
        callback(eventName, data);
      });

      return true;
    } catch (error) {
      return null;
    }
  }

  /**
   * Escucha un evento en un canal privado
   * @param channel - Nombre del canal (sin prefijo "private-")
   * @param event - Nombre del evento
   * @param callback - Función a ejecutar cuando se recibe el evento
   * @returns El canal creado o una referencia booleana si se utilizó Echo
   */
  listenPrivate(channel: string, event: string, callback: Function) {
    if (!this.isInitialized) {
      return null;
    }

    try {
      // Preparar nombres normalizados
      const privateChannelName = `private-${channel}`;
      const pusherEventName = event.startsWith('.') ? event : `.${event}`;
      const echoEventName = event.startsWith('.') ? event.slice(1) : event;

      // Asegurar desvincular del canal privado correcto
      this.unsubscribeFromEvent(privateChannelName, pusherEventName);


      const existingChannel = this.pusher.channel(privateChannelName);

      let privateChannel;

      if (existingChannel) {
        privateChannel = existingChannel;
        privateChannel.unbind(pusherEventName);
        privateChannel.bind(pusherEventName, (data: any) => {
          callback(data);
        });
      } else {
        // Echo.listen añade un punto automáticamente, por eso usamos echoEventName
        this._echo.private(channel).listen(echoEventName, (data: any) => {
          callback(data);
        });
      }

      return existingChannel || true;
    } catch (error) {
      return null;
    }
  }

  /**
   * Abandona todos los canales suscritos
   */
  leaveChannels() {
    this._echo.leaveAllChannels();
  }

  /**
   * Abandona un canal específico
   * @param channel - Nombre completo del canal (con prefijo si es privado)
   */
  leaveThisChannel(channel: string) {
    this._echo.leaveChannel(channel);
  }

  /**
   * Cancela la suscripción a un evento específico en un canal
   * @param channelName - Nombre completo del canal
   * @param eventName - Nombre del evento
   */
  unsubscribeFromEvent(channelName: string, eventName: string): void {
    try {
      const channel = this.pusher.channel(channelName);
      if (channel) {
        channel.unbind(eventName);
      }
    } catch (error) {
      // Error silencioso
    }
  }

  /**
   * Fuerza la desconexión de un canal específico
   * limpiando todos sus eventos y suscripciones
   * @param channelName - Nombre del canal (sin prefijo "private-")
   */
  forceDisconnectChannel(channelName: string): void {
    try {
      const privateChannelName = `private-${channelName}`;

      if (this.pusher) {
        const channel = this.pusher.channel(privateChannelName);

        if (channel) {
          channel.unbind_all();
          this.pusher.unsubscribe(privateChannelName);
        }

        const channelWithoutPrefix = this.pusher.channel(channelName);
        if (channelWithoutPrefix) {
          channelWithoutPrefix.unbind_all();
          this.pusher.unsubscribe(channelName);
        }
      }
    } catch (error) {
      // Error silencioso
    }
  }

  /**
   * Obtiene la instancia de Echo si está inicializada
   * @returns Instancia de Echo o null si no está inicializada
   */
  get echo(): Echo<'pusher'> | null {
    return this.isInitialized ? this._echo : null;
  }
}
